<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton
            :class="{ 'opacity-25': isRedirectingForwardFromFares }"
            :disabled="isRedirectingForwardFromFares"
            @click="router.back"
          />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{ t('fareFamily.title') }}
          </h2>
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div
        v-if="details.isLoading.value"
        class="flex flex-grow items-center justify-center"
      >
        <FreeLoader />
      </div>
      <div
        v-else-if="
          bookingStore.offer &&
          bookingStore.passengersSchema &&
          bookingStore.dict
        "
        class="mb-24 space-y-4 pb-4"
      >
        <FareFamilyNotice
          v-if="shouldShowMarkupNotice"
          :text="t('fareFamily.markupNotice')"
        />
        <div class="space-y-4">
          <SmartRoute
            v-if="isSmartRoute"
            @onLabelClick="bottomSheet.show(smartRouteBottomSheetName)"
          />
          <FareFamilyNotice
            v-if="isBaseFareFamily"
            :text="t('fareFamily.baseFfNoticeText')"
          />
          <div
            v-for="(groupChoices, groupKey, groupIdx) in fareGroups"
            :key="groupKey"
            class="space-y-4"
          >
            <template v-if="Object.keys(fareGroups).length > 1">
              <FareSegmentCard
                :group-key="groupKey as string"
                :group-idx="groupIdx"
                :flight-group="flightGroups[groupKey]"
                :choices="groupChoices"
                :selected-fare="selectedFares[groupKey as string] ?? null"
                :is-missing="
                  segmentGroupsMissingFares.includes(groupKey as string)
                "
                @onOpenFlightDetails="onOpenFlightDetails"
                @onOpenFareSelect="onOpenFareSelect"
              />
            </template>
            <template v-else>
              <FareCard
                v-for="(choice, idx) in groupChoices"
                :key="choice.id"
                :fare-idx="idx + 1"
                :choice="choice"
                :provider-class="providerClass"
                :is-domestic="bookingStore.offer?.$meta?.is_domestic"
                @onFareSelect="onFareSelect({ groupKey, choice })"
              >
                <template
                  #cashback="{
                    cashbackAmount,
                    isCashbackError,
                    isCashbackLoading,
                  }"
                >
                  <div
                    v-if="
                      !isCashbackError &&
                      !isCashbackLoading &&
                      cashbackAmount > 0
                    "
                    class="freedom-background-color rounded-full px-1.5 py-0.5"
                  >
                    <i18n-t
                      class="freedom-loyalty-text text-xs font-semibold"
                      keypath="details.cashback"
                      tag="p"
                    >
                      <template #amount>
                        <span v-price="cashbackAmount" />
                      </template>
                    </i18n-t>
                  </div>
                </template>
              </FareCard>
            </template>
          </div>
        </div>
      </div>
      <BookingErrorModal :name="bookingErrorModalName">
        <template #action>
          <FreeButton
            type="button"
            class="gray-button w-full"
            @click="handleStartNewSearch"
          >
            {{ t('booking.startNewSearch') }}
          </FreeButton>
        </template>
      </BookingErrorModal>
      <SmartRouteBottomSheet :name="smartRouteBottomSheetName" />
      <template v-if="Object.keys(fareGroups).length > 1">
        <FareFamilyBottomSheet @onFareSelect="onFareSelect" />
        <FareFlightDetailsBottomSheet
          :dict="details.dict"
          :city-mismatches="details.offer.value?.$meta.city_mismatch"
        />
        <template v-if="Object.keys(selectedFares).length > 0">
          <PriceAndButtonCard
            source-page="fareFamilyPage"
            :price-amount="Number(ticketPrice)"
            @onButtonAction="onSubmit"
          />
        </template>
      </template>
      <Teleport to="body">
        <div class="fixed flex w-full justify-center">
          <FreeAlertWithUseAlert
            :use-alert="createAlertInstance"
            :icons="{ warning: RedWarningIcon }"
            class="bottom-28 mx-4"
          >
            <template #default="{ content }">
              <span class="text-text-neutral-primary">
                {{ content.message }}
              </span>
            </template>
          </FreeAlertWithUseAlert>
        </div>
      </Teleport>
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import type { AxiosError } from 'axios';
  import { useDetails } from '@aviata/booking-composable';
  import { controllerModel } from 'shared';
  import {
    FreeBackButton,
    FreeButton,
    FreeHeader,
    FreeLoader,
    FreePageLayout,
    FreeAlertWithUseAlert,
  } from 'freedom-shared';

  import { SmartRoute, SmartRouteBottomSheet } from '@/pages/offer-details/ui';

  import { ancillaryBaggageModel } from '@/widgets/ancillary-baggage';

  import { bookingStateModel } from '@/features/booking-state';
  import { PriceAndButtonCard } from '@/features/price-and-button-card';
  import {
    FareCard,
    FareFamilyBottomSheet,
    FareFlightDetailsBottomSheet,
    FareSegmentCard,
    type FareFamilyChoiceSpec,
    useFareFamily,
    useFareFamilyBottomSheet,
    useFareFlightDetailsBottomSheet,
  } from '@/features/fare-family';

  import { getOfferDetails, isSmartOffer } from '@/entities/offer';
  import { BookingErrorModal } from '@/entities/booking-error-modal';
  import {
    FareFamilyNotice,
    markupNoticeModel,
  } from '@/entities/markup-notice';

  import { fetchFareRules } from '@/shared/api';
  import { sentryModel } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';
  import { useAlert } from '@/shared/ui';

  import RedWarningIcon from '@/assets/images/red-warning.svg?component';

  import type { BookingError } from '../../types';

  const props = defineProps<{
    offerId: string;
    resultId: string;
  }>();

  const router = useRouter();

  const ALERT_NAMESPACE = 'fare-family-page';
  const createAlertInstance = () => useAlert(ALERT_NAMESPACE);
  const alert = createAlertInstance();

  const modal = controllerModel.useModal();
  const bottomSheet = controllerModel.useBottomSheet();

  const fareFamilyGroupBottomSheet = useFareFamilyBottomSheet();
  const fareFlightDetailsBottomSheet = useFareFlightDetailsBottomSheet();

  const { t } = useI18n();

  const bookingStore = bookingStateModel.useBookingStore();
  const ancillariesStore = ancillaryBaggageModel.useAncillariesStore();
  const bookingErrorModalName = 'booking-details-error-modal';
  const smartRouteBottomSheetName = 'smart-route-bottom-sheet';
  /**
   * Need to prevent bug which happens when user tries to go back
   * during redirection from fare page forward.
   * @see https://tracker.yandex.ru/CHCTRVL-8439
   */
  const isRedirectingForwardFromFares = ref(false);

  const details = useDetails({
    offerId: props.offerId,
    resultId: props.resultId,
    fetchDetails: (resultId, offerId) => getOfferDetails({ resultId, offerId }),
    fetchFareRules,
    updateFare: (resultId, offerId, fareFamilyId) =>
      getOfferDetails({ resultId, offerId, fareFamilyId }),
    onInit(details) {
      bookingStore.init(details);
      bookingStore.update({ ticketPrice: details.offer.price.amount });
    },
    onInitError(error: AxiosError<{ errors: BookingError[] }>) {
      sentryModel.logBaseError({
        error,
        description: 'Booking error: failed to init page',
      });
      const errorCode = error?.response?.data?.errors[0]?.code;
      const isNotDefaultError =
        errorCode === 'offer_expired' || errorCode === 'not_found';
      modal.show(bookingErrorModalName, {
        ...(isNotDefaultError && {
          title: t('booking.errors.expiredOffer.title'),
          message: t('booking.errors.expiredOffer.description'),
        }),
        persistent: true,
      });
    },
    onFareChange(details) {
      ancillariesStore.clearState();
      bookingStore.onUpdateFlights(details);
      bookingStore.update({ ticketPrice: details.offer.price.amount });
      router.push({
        name: 'PassengersDataPage',
        params: { source: router.currentRoute.value.params.source },
      });
      isRedirectingForwardFromFares.value = false;
    },
    onFareChangeError(error) {
      sentryModel.logBaseError({
        error,
        description: 'Booking error: failed to change fare',
      });
      modal.show(bookingErrorModalName);
      isRedirectingForwardFromFares.value = false;
    },
  });

  const { shouldShowMarkupNotice } = markupNoticeModel.useMarkupNotice(
    details.offer,
  );

  const segmentGroupsMissingFares = ref<string[]>([]);

  const isSmartRoute = computed(() =>
    details.offer.value ? isSmartOffer(details.offer.value) : false,
  );

  const {
    ticketPrice,
    flightGroups,
    fareGroups,
    selectedFares,
    prepareFarePayload,
    selectFare,
    isBaseFareFamily,
  } = useFareFamily({
    offer: details.offer,
    dict: details.dict,
  });

  const providerClass = computed(
    () => details.offer.value?.$meta?.provider_class,
  );

  const handleStartNewSearch = () => {
    modal.hide(bookingErrorModalName);
    router.replace({ name: 'MainPage' });
  };

  function changeFare() {
    isRedirectingForwardFromFares.value = true;

    if (isBaseFareFamily.value) {
      router.push({
        name: 'PassengersDataPage',
        params: { source: router.currentRoute.value.params.source },
      });
      return;
    }

    details.changeFare(prepareFarePayload());
  }

  function onFareSelect(payload: {
    groupKey: string;
    choice: FareFamilyChoiceSpec;
  }) {
    selectFare(payload);

    if (Object.keys(fareGroups.value).length > 1) {
      segmentGroupsMissingFares.value = [];
      return;
    }

    changeFare();

    analyticsEventsLib.continueFareFamily({
      isFareFamiliesFlexible: false,
      isError: false,
    });
  }

  function validateSelectedFares() {
    segmentGroupsMissingFares.value = Object.keys(fareGroups.value).filter(
      key => !(key in selectedFares),
    );

    if (segmentGroupsMissingFares.value.length > 0) {
      alert.show({
        type: 'warning',
        message: t('fareFamily.alert.selectFare'),
        duration: 3000,
      });
      return false;
    }

    return true;
  }

  function onSubmit() {
    const isAllSegmentCoveredByFares = validateSelectedFares();

    if (isAllSegmentCoveredByFares) {
      changeFare();
    }

    analyticsEventsLib.continueFareFamily({
      isFareFamiliesFlexible: true,
      isError: !isAllSegmentCoveredByFares,
    });
  }

  function onOpenFlightDetails({ groupKey }: { groupKey: string }) {
    fareFlightDetailsBottomSheet.show({
      flights: [flightGroups.value[groupKey]],
    });
  }

  function onOpenFareSelect({ groupKey }: { groupKey: string }) {
    if (details.dict.value) {
      fareFamilyGroupBottomSheet.show({
        groupKey,
        choices: fareGroups.value[groupKey],
        selectedId: selectedFares[groupKey]?.id,
        isDomestic: bookingStore.offer?.$meta?.is_domestic,
        segments: {
          dep: flightGroups.value[groupKey].segments[0].dep.city,
          arr: flightGroups.value[groupKey].segments[
            flightGroups.value[groupKey].segments.length - 1
          ].arr.city,
        },
      });
    }
  }

  onMounted(() => {
    details.init();
    analyticsEventsLib.fareFamilyPageOpen();
  });
</script>
